<template>
  <section class="stakeholders">
     <div class="stakeholders-content">
      <!-- Main Carousel -->
      <div class="relative flex items-center justify-center min-h-[calc(100vh-160px)]">
        <!-- Enhanced Navigation Arrows -->
        <button
          @click="previousSlide"
          class="absolute left-12 z-40 group"
        >
          <div class="glass-effect-blue w-20 h-20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 shadow-2xl group-hover:shadow-yellow-400/20 border border-white/20">
            <svg class="w-10 h-10 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        </button>

        <button
          @click="nextSlide"
          class="absolute right-12 z-40 group"
        >
          <div class="glass-effect-blue w-20 h-20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 shadow-2xl group-hover:shadow-yellow-400/20 border border-white/20">
            <svg class="w-10 h-10 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>

        <!-- Carousel Cards - Content Based -->
        <div class="relative w-full max-w-[90rem] mx-auto px-8">
          <div class="relative h-[700px] flex items-center justify-center">
            <div
              v-for="(feature, index) in features"
              :key="feature.title"
              class="absolute w-[600px] h-[450px] cursor-pointer group"
              :style="getCardPosition(index)"
              @click="goToSlide(index)"
            >
              <div class="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl group-hover:shadow-[0_25px_50px_-12px_rgba(251,191,36,0.4)] transition-all duration-500">
                <!-- Background Gradient -->
                <div class="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-indigo-600/30" />
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
                
                <!-- Border -->
                <div class="absolute inset-0 rounded-3xl border border-white/20"></div>

                <!-- Content -->
                <div class="relative p-12 flex flex-col h-full text-white">
                  <!-- Icon -->
                  <div class="mb-8">
                    <div class="glass-effect-blue w-16 h-16 rounded-2xl flex items-center justify-center border border-white/20">
                      <div class="feature-icon" v-html="feature.icon"></div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h2 class="text-2xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
                    {{ feature.title }}
                  </h2>

                  <!-- Description -->
                  <p class="text-white/95 text-base leading-relaxed line-clamp-6 drop-shadow-md">
                    {{ feature.detail }}
                  </p>
                </div>

                <!-- Center Highlight Effect -->
                <div v-if="index === currentIndex" class="absolute inset-0 border-2 border-yellow-400/50 rounded-3xl animate-pulse-glow"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Pagination Dots -->
        <div class="absolute bottom-12 left-1/2 transform -translate-x-1/2">
          <div class="glass-effect-blue px-6 py-3 rounded-full border border-white/20">
            <div class="flex space-x-3">
              <button
                v-for="(_, index) in features"
                :key="index"
                @click="goToSlide(index)"
                :class="[
                  'transition-all duration-300',
                  index === currentIndex
                    ? 'w-8 h-3 bg-yellow-400 rounded-full animate-pulse-glow'
                    : 'w-3 h-3 bg-white/40 hover:bg-white/60 rounded-full'
                ]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'

// 캐러셀 상태 관리
const currentIndex = ref(0)

// 파티클 스타일은 AnimatedBackground 컴포넌트에서 처리

// 캐러셀 네비게이션 함수들
const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % features.length
}

const previousSlide = () => {
  currentIndex.value = (currentIndex.value - 1 + features.length) % features.length
}

const goToSlide = (index) => {
  currentIndex.value = index
}

// 카드 위치 계산 (React 코드의 getCardPosition 함수)
const getCardPosition = (index) => {
  const diff = index - currentIndex.value
  const totalSlides = features.length
  
  let normalizedDiff = diff
  if (Math.abs(diff) > totalSlides / 2) {
    normalizedDiff = diff > 0 ? diff - totalSlides : diff + totalSlides
  }

  const isCenter = normalizedDiff === 0
  const isVisible = Math.abs(normalizedDiff) <= 2

  if (!isVisible) return { display: 'none' }

  const translateX = normalizedDiff * 520 // 520px spacing for larger cards
  const scale = isCenter ? 1 : 0.75
  const opacity = isCenter ? 1 : 0.5
  const zIndex = isCenter ? 30 : 20 - Math.abs(normalizedDiff)

  return {
    transform: `translateX(${translateX}px) scale(${scale})`,
    opacity,
    zIndex,
    transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  }
}

// 자동 슬라이드 (6초마다)
const startAutoSlide = () => {
  setInterval(() => {
    nextSlide()
  }, 6000)
}


onMounted(() => {
  startAutoSlide()
})

const features = [
  {
    title: "AITRA가 기업 업무 속도를 재정의합니다.",
    //subtitle: "언어가 아닌, 전략으로 경쟁하라",
    detail: "글로벌 무대에서 경쟁의 본질은 '언어'가 아니라 '속도'입니다. AITRA는 회의가 멈추지 않도록, 발언과 동시에 번역과 합성이 이루어지는 AI 실시간 다국어 커뮤니케이션 엔진을 제공합니다. 통역 대기나 인력 조율 없이, 모든 언어가 하나의 흐름으로 연결됩니다. 회의가 끝나는 순간, 자동 요약 리포트와 다국어 아카이브가 완성되어 의사결정은 더 빠르게, 협업은 더 단단하게 이어집니다.",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>`
  },
  {
    title: "AITRA가 당신을 세계에 연결합니다.",
    //subtitle: "작은 팀도 세계와 연결된다",
    detail: "AITRA는 예산이나 인력의 한계를 넘어, 누구나 글로벌 회의에 바로 참여할 수 있는 AI 커뮤니케이션 허브입니다. 별도의 번역가 없이도 즉시 대화가 가능하며, 모든 대화와 자료가 자동으로 기록·정리되어 고객 및 파트너 관리의 기반 데이터가 됩니다. 언어가 아니라 의미로 연결되는 협업, AITRA는 작은 팀에게도 글로벌 시장으로 향하는 문을 여는 도구입니다.",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>`
  },
  {
    title: "AITRA는 언어의 경계를 넘어, 이해관계자 가치에 집중합니다.",
    //subtitle: "언어의 차이를 이해의 기회로 바꾸다",
    detail: "다양한 언어와 문화가 공존하는 국제 무대에서, AITRA는 예산 제약 없는 AI 통합 회의 환경을 제공합니다. 참가자들은 각자의 언어로 발언하고, AITRA는 그 의미를 모든 언어로 실시간 공유합니다. 회의가 끝나면, AI가 생성한 다국어 리포트가 모든 참여자에게 동일하게 제공되어 언어에 관계없이 모두가 같은 정보로 토론하고 결정할 수 있습니다.",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>`
  }
]
</script>

<style scoped>
.stakeholders {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  background: transparent; /* 공통 배경 사용 */
}

.stakeholders-content {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100vh;
}

/* 배경 관련 CSS는 AnimatedBackground 컴포넌트에서 처리 */

/* Glass Effect */
.glass-effect-blue {
  background: rgba(59, 130, 246, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(147, 197, 253, 0.2);
  box-shadow: 0 8px 32px rgba(29, 78, 216, 0.15);
}

/* 애니메이션 */
@keyframes float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  33% {
    transform: translateY(-20px) translateX(10px);
  }
  66% {
    transform: translateY(10px) translateX(-5px);
  }
}

@keyframes pulse-glow {
  0%, 100% { box-shadow: 0 0 5px rgba(251, 191, 36, 0.5); }
  50% { box-shadow: 0 0 20px rgba(251, 191, 36, 0.8), 0 0 30px rgba(251, 191, 36, 0.6); }
}

.animate-pulse-glow {
  animation: pulse-glow 2s ease-in-out infinite;
}

/* Feature Icon Styling */
.feature-icon {
  width: 2rem;
  height: 2rem;
  color: #fbbf24; /* yellow-400 */
}

.feature-icon svg {
  width: 100%;
  height: 100%;
}

/* Line Clamp Utility */
.line-clamp-6 {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.feature-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.feature-header {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
}

.feature-icon {
  width: 3rem;
  height: 3rem;
  color: #0033A0; /* AJOU BLUE */
  padding: 0.625rem;
  background: linear-gradient(135deg, rgba(0, 51, 160, 0.1), rgba(146, 205, 220, 0.15));
  border-radius: 1rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.feature-card:hover .feature-icon {
  background: linear-gradient(135deg, rgba(0, 51, 160, 0.15), rgba(146, 205, 220, 0.2));
  transform: scale(1.05);
}

.feature-title-group {
  flex: 1;
}

.feature-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0033A0; /* AJOU BLUE */
  line-height: 1.3;
  margin-bottom: 0.5rem;
}

.feature-subtitle {
  font-size: 1rem;
  font-weight: 600;
  color: #C4961A; /* AJOU GOLD */
  line-height: 1.4;
  margin: 0;
}

.feature-detail {
  font-size: 0.95rem;
  line-height: 1.75;
  color: #555555;
  padding-left: 4.25rem;
}

@media (max-width: 768px) {
  .stakeholders {
    padding: 2rem 1rem;
  }

  .features-grid {
    gap: 1.5rem;
  }

  .feature-card {
    padding: 1.5rem;
    gap: 1rem;
  }

  .feature-header {
    gap: 1rem;
  }

  .feature-icon {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0.5rem;
  }

  .feature-title {
    font-size: 1.2rem;
  }

  .feature-subtitle {
    font-size: 0.9rem;
  }

  .feature-detail {
    font-size: 0.9rem;
    padding-left: 3.5rem;
  }
}
</style> 